"use client";

import React, { useState, useEffect, useRef, useCallback } from "react";
import { ref, onValue, push, set, update } from "firebase/database";
import { db, auth } from "../Firebase/firestore";
import FileStorage from "./fileStorage";
import {
  IoDocumentOutline,
  IoCheckbox,
  IoCheckboxOutline,
  IoTrashBinOutline,
  IoAddOutline,
  IoAttachOutline,
  IoLayersOutline,
  IoSearchOutline,
  IoEllipsisVerticalOutline,
  IoExitOutline,
  IoArrowUndoOutline,
  IoImagesOutline,
  IoLinkOutline,
  IoCloseOutline,
} from "react-icons/io5";
import "./chatbot.css";
import { FaCopy, FaEdit, FaReply, FaTimes, FaTrash } from "react-icons/fa";

const SupplierChatbot = () => {
  const [chats, setChats] = useState([]);
  const [groupChats, setGroupChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [replyMessage, setReplyMessage] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [userIsTyping, setUserIsTyping] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [showFilePreview, setShowFilePreview] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [showTemplates, setShowTemplates] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [showCreateTemplate, setShowCreateTemplate] = useState(false);
  const [newTemplate, setNewTemplate] = useState({ title: "", message: "" });
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedChats, setSelectedChats] = useState([]);
  const [showBulkMessage, setShowBulkMessage] = useState(false);
  const [bulkMessage, setBulkMessage] = useState("");
  const [bulkFiles, setBulkFiles] = useState([]);
  const [isSendingBulk, setIsSendingBulk] = useState(false);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [selectedGroupUsers, setSelectedGroupUsers] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const [memberProfiles, setMemberProfiles] = useState({});
  const [showChatMenu, setShowChatMenu] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showUserProfile, setShowUserProfile] = useState(false);
  const [selectedUserProfile, setSelectedUserProfile] = useState(null);
  const [showGroupProfile, setShowGroupProfile] = useState(false);
  const [selectedGroupProfile, setSelectedGroupProfile] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null);
  const [highlightedMessage, setHighlightedMessage] = useState(null);
  const [showSearch, setShowSearch] = useState(false);
  const [showMediaModal, setShowMediaModal] = useState(false);
  const [mediaModalType, setMediaModalType] = useState("media");
  const [chatMedia, setChatMedia] = useState([]);
  const [showAddMember, setShowAddMember] = useState(false);
  const [showRemoveMember, setShowRemoveMember] = useState(false);
  const [selectedMembersToAdd, setSelectedMembersToAdd] = useState([]);
  const [selectedMembersToRemove, setSelectedMembersToRemove] = useState([]);
  const [senderDisplayMap, setSenderDisplayMap] = useState({});
  const [allSidebarUsers, setAllSidebarUsers] = useState([]);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleteMsg, setDeleteMsg] = useState(null);
  const [menuMessageId, setMenuMessageId] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState(null);

  const [swipeState, setSwipeState] = useState({
    startX: 0,
    currentX: 0,
    isDragging: false,
    messageId: null,
  });
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const bulkFileInputRef = useRef(null);
  const chatMenuRef = useRef(null);
  const searchRef = useRef(null);

  function getMessageTime(msg) {
    const raw = msg.timestamp || msg.createdAt;
    if (!raw) return null;
    const date = new Date(Number(raw));
    if (isNaN(date.getTime())) return null;
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }

  const fetchUserInfo = async (userId) => {
    try {
      const rootRef = ref(db, "profileDashboard");
      const snapshot = await new Promise((resolve) =>
        onValue(rootRef, resolve, { onlyOnce: true }),
      );

    const allProfiles = snapshot.val() || {};

      // Loop through all main users
      for (const mainUid in allProfiles) {
        const mainProfile = allProfiles[mainUid];
        const parentCompanyName =
          mainProfile.company_name ||
          mainProfile.company_name ||
          "Unknown Company";

        // 1. Main user
        if (mainUid === userId) {
          return {
            name: parentCompanyName,
            phone: mainProfile.phone || "Not available",
          };
        }

        // 2. Firms
        if (mainProfile.firms) {
          for (const firmUid in mainProfile.firms) {
            const firm = mainProfile.firms[firmUid];
            if (firmUid === userId) {
              return {
                name: `Firm | ${firm.company_name || "Unknown Firm"} | ${parentCompanyName}`,
                phone: firm.phone || mainProfile.phone || "Not available",
              };
            }
          }
        }

        // 3. Partners
        if (mainProfile.partners) {
          for (const partnerUid in mainProfile.partners) {
            if (partnerUid === userId) {
              // fetch actual partner profile
              const partnerProfile = allProfiles[partnerUid] || {};

              return {
                name: `${partnerProfile.name || partnerProfile.company_name || "Partner"} | ${parentCompanyName}`,
                phone:
                  partnerProfile.phone || mainProfile.phone || "Not available",
              };
            }
          }
        }
      }

      // Not found anywhere
      return {
        name: "User not found",
        phone: "Not available",
      };
    } catch (error) {
      console.error("Error fetching user info:", error);
      return {
        name: "User not found",
        phone: "Not available",
      };
    }
  };

  const fetchUserProfile = async (userId) => {
    try {
      const rootRef = ref(db, "profileDashboard");
      const snapshot = await new Promise((resolve) =>
        onValue(rootRef, resolve, { onlyOnce: true }),
      );

      const allProfiles = snapshot.val() || {};

      // 1. Direct match → Main user OR Firm (standalone profile)
      if (allProfiles[userId]) {
        return allProfiles[userId];
      }

      // 2. Search inside partners of all main users
      for (const mainUid in allProfiles) {
        const mainProfile = allProfiles[mainUid];

        if (mainProfile.partners) {
          for (const partnerUid in mainProfile.partners) {
            if (partnerUid === userId) {
              const partner = mainProfile.partners[partnerUid];

              // Return ONLY required fields for partner
              return {
                uid: partnerUid,
                name: partner.name || "Unknown Partner",
                email: partner.email || "Not available",
              };
            }
          }
        }
      }

      const transporterRef = ref(db, `transporter-profile/${userId}`);

const transporterSnapshot = await new Promise((resolve) =>
  onValue(transporterRef, resolve, { onlyOnce: true }),
);

const transporter = transporterSnapshot.val();

if (transporter) {
  return {
    name:
      transporter.contactPerson ||
      transporter.company ||
      "Unknown Transporter",
    phone: transporter.phone || "Not available",
  };
}

return {
  name: "User not found",
  phone: "Not available",
};

      // User not found in either database location
      return null;
    } catch (error) {
      console.error("Error fetching user profile:", error);
      return null;
    }
  };

  const fetchAllUsersForSidebar = async () => {
    try {
      const rootRef = ref(db, "profileDashboard");

      const snapshot = await new Promise((resolve) =>
        onValue(rootRef, resolve, { onlyOnce: true }),
      );

      const allProfiles = snapshot.val() || {};

      const users = [];
      const addedIds = new Set();

      // Fetch transporters
      const transporterRef = ref(db, "transporter-profile");
      const transporterSnapshot = await new Promise((resolve) =>
        onValue(transporterRef, resolve, { onlyOnce: true }),
      );

      const allTransporters = transporterSnapshot.val() || {};

      for (const transporterUid in allTransporters) {
        if (!addedIds.has(transporterUid)) {
          const transporter = allTransporters[transporterUid];

          users.push({
            userId: transporterUid,
            companyName:
              transporter.company ||
              transporter.contactPerson ||
              "Unknown Transporter",
            userType: transporter.type || "transporter",
            isGroup: false,
            messages: [],
            lastMessage: null,
            unreadCount: 0,
            chatStatus: { status: "open" },
            uniqueId: `sidebar-${transporterUid}`,
            isSidebarOnly: true,
            isTransporter: true,
          });

          addedIds.add(transporterUid);
        }
      }

      for (const mainUid in allProfiles) {
        const mainProfile = allProfiles[mainUid] || {};

        // Main user
        if (!addedIds.has(mainUid)) {
          users.push({
            userId: mainUid,
            userType: mainProfile.type || "client",
            companyName:
              mainProfile.company_name || mainProfile.name || "Unknown User",
            isGroup: false,
            messages: [],
            lastMessage: null,
            unreadCount: 0,
            chatStatus: { status: "open" },
            uniqueId: `sidebar-${mainUid}`,
            isSidebarOnly: true,
          });

          addedIds.add(mainUid);
        }

        // Firms
        if (mainProfile.firms) {
          for (const firmUid in mainProfile.firms) {
            if (!addedIds.has(firmUid)) {
              const firm = mainProfile.firms[firmUid];

              users.push({
                userId: firmUid,
                companyName: firm.company_name || firm.name || "Unknown Firm",
                userType: firm.type || "firm",
                isGroup: false,
                messages: [],
                lastMessage: null,
                unreadCount: 0,
                chatStatus: { status: "open" },
                uniqueId: `sidebar-${firmUid}`,
                isSidebarOnly: true,
              });

              addedIds.add(firmUid);
            }
          }
        }

        // Partners
        if (mainProfile.partners) {
          for (const partnerUid in mainProfile.partners) {
            if (!addedIds.has(partnerUid)) {
              const partner = mainProfile.partners[partnerUid];

              users.push({
                userId: partnerUid,
                companyName: `${partner.name || partner.company_name || "Partner"} | ${mainProfile.company_name || "Unknown Company"}`,
                userType: partner.type || "client",
                isGroup: false,
                messages: [],
                lastMessage: null,
                unreadCount: 0,
                chatStatus: { status: "open" },
                uniqueId: `sidebar-${partnerUid}`,
                isSidebarOnly: true,
              });

              addedIds.add(partnerUid);
            }
          }
        }
      }

      setAllSidebarUsers(users);
    } catch (error) {
      console.error("Error fetching sidebar users:", error);
    }
  };

  const extractChatMedia = (chat) => {
    const media = [];
    const docs = [];
    const links = [];

    chat.messages.forEach((msg) => {
      if (msg.files) {
        msg.files.forEach((file) => {
          const fileData = {
            ...file,
            messageId: msg.id,
            timestamp: msg.timestamp,
            sender: msg.sender,
          };
          if (file.type.startsWith("image/")) {
            media.push(fileData);
          } else {
            docs.push(fileData);
          }
        });
      }
      const messageText = msg.message || msg.reply || "";
      const urlRegex = /(https?:\/\/[^\s]+)/g;
      const foundLinks = messageText.match(urlRegex);
      if (foundLinks) {
        foundLinks.forEach((link) => {
          links.push({
            url: link,
            messageId: msg.id,
            timestamp: msg.timestamp,
            sender: msg.sender,
            messageText:
              messageText.substring(0, 100) +
              (messageText.length > 100 ? "..." : ""),
          });
        });
      }
    });
    return { media, docs, links };
  };

  const showMediaVisibility = (chat, type = "media") => {
    const { media, docs, links } = extractChatMedia(chat);
    setChatMedia({ media, docs, links });
    setMediaModalType(type);
    setShowMediaModal(true);
  };

  useEffect(() => {
    fetchAllUsersForSidebar();
  }, []);

  useEffect(() => {
    const chatsRef = ref(db, "userchatbox");
    const unsubscribe = onValue(
      chatsRef,
      async (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const chatList = [];
          for (const userId of Object.keys(data)) {
            for (const chatId of Object.keys(data[userId])) {
              const chatData = data[userId][chatId];
              const userInfo = await fetchUserInfo(userId);
              const messages = Object.entries(chatData)
                .filter(
                  ([key]) =>
                    key !== "companyName" &&
                    key !== "chatStatus" &&
                    key !== "supplierTyping" &&
                    key !== "userTyping",
                )
                .map(([id, msg]) => ({ id, ...msg }))
                .sort(
                  (a, b) => Number(a.timestamp || 0) - Number(b.timestamp || 0),
                );
              const lastMessage =
                messages.length > 0 ? messages[messages.length - 1] : null;
              chatList.push({
                userId,
                chatId,
                uniqueId: `${userId}-${chatId}`,
                companyName: userInfo.name,
                userType:
                  allSidebarUsers.find((user) => user.userId === userId)
                    ?.userType || "",
                messages,
                lastMessage,
                unreadCount: Object.values(chatData)
                  .filter((msg) => msg.sender === "user") // only messages from user
                  .filter((msg) => {
                    const lastRead = chatData.lastReadBySupplier || 0; // fallback to 0 if missing
                    return Number(msg.timestamp) > lastRead;
                  }).length,
                chatStatus: chatData.chatStatus || { status: "open" },
                isGroup: false,
              });
            }
          }
          chatList.sort((a, b) => {
            const aTime = a.lastMessage?.timestamp
              ? Number(a.lastMessage.timestamp)
              : 0;
            const bTime = b.lastMessage?.timestamp
              ? Number(b.lastMessage.timestamp)
              : 0;
            return bTime - aTime;
          });
          setChats(chatList);
          if (selectedChat && !selectedChat.isGroup) {
            const updatedChat = chatList.find(
              (c) => c.uniqueId === selectedChat.uniqueId,
            );
            if (
              updatedChat &&
              JSON.stringify(updatedChat.messages) !==
                JSON.stringify(selectedChat.messages)
            ) {
              setSelectedChat(updatedChat);
            }
          }
        } else {
          setChats([]);
          if (selectedChat && !selectedChat.isGroup) {
            setSelectedChat(null);
          }
        }
      },
      (error) => {
        console.error("Error fetching chats:", error);
        setChats([]);
      },
    );
    return () => unsubscribe();
  }, [selectedChat]);

  useEffect(() => {
    const groupChatsRef = ref(db, "groupChats");
    const unsubscribe = onValue(
      groupChatsRef,
      (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const groupChatList = Object.keys(data).map((groupId) => {
            const groupData = data[groupId] || {};
            const messagesArray = Object.entries(groupData.messages || {}).map(
              ([id, msg]) => ({
                id,
                ...msg,
                timestamp: Number(msg.timestamp) || 0,
              }),
            );
            const sortedMessages = messagesArray.sort(
              (a, b) => a.timestamp - b.timestamp,
            );
            const lastMessage =
              sortedMessages.length > 0
                ? sortedMessages[sortedMessages.length - 1]
                : null;
            return {
              groupId,
              uniqueId: `group-${groupId}`,
              groupName: groupData.groupName || `Group-${groupId}`,
              members: groupData.members || [],
              groupAdmin: groupData.groupAdmin || "supplier",
              messages: sortedMessages,
              lastMessage,
              unreadCount: messagesArray.filter(
                (msg) =>
                  msg.sender !== "supplier" &&
                  !(msg.readBy || []).includes(auth.currentUser.uid),
              ).length,
              isGroup: true,
            };
          });
          groupChatList.sort((a, b) => {
            const aTime = a.lastMessage?.timestamp
              ? Number(a.lastMessage.timestamp)
              : 0;
            const bTime = b.lastMessage?.timestamp
              ? Number(b.lastMessage.timestamp)
              : 0;
            return bTime - aTime;
          });
          setGroupChats(groupChatList);
          if (selectedChat && selectedChat.isGroup) {
            const updatedChat = groupChatList.find(
              (c) => c.uniqueId === selectedChat.uniqueId,
            );
            if (
              updatedChat &&
              JSON.stringify(updatedChat.messages) !==
                JSON.stringify(selectedChat.messages)
            ) {
              setSelectedChat(updatedChat);
            }
          }
        } else {
          setGroupChats([]);
          if (selectedChat && selectedChat.isGroup) {
            setSelectedChat(null);
          }
        }
      },
      (error) => {
        console.error("Error fetching group chats:", error);
        setGroupChats([]);
      },
    );
    return () => unsubscribe();
  }, [selectedChat]);

  useEffect(() => {
    if (selectedChat && selectedChat.isGroup) {
      const fetchProfiles = async () => {
        const profiles = {};
        for (const userId of selectedChat.members) {
          profiles[userId] = await fetchUserInfo(userId);
        }
        setMemberProfiles(profiles);
      };
      fetchProfiles();
    } else {
      setMemberProfiles({});
    }
  }, [selectedChat]);

  useEffect(() => {
    const loadSenderDisplayInfo = async () => {
      if (!selectedChat || !selectedChat.messages) return;

      const map = {};

      for (const msg of selectedChat.messages) {
        if (msg.sender && msg.sender !== "supplier" && !map[msg.sender]) {
          const info = await fetchUserInfo(msg.sender);

          map[msg.sender] = info.name || "User not found";
        }
      }

      setSenderDisplayMap(map);
    };

    loadSenderDisplayInfo();
  }, [selectedChat]);

  useEffect(() => {
    let unsubscribeTyping;
    if (selectedChat) {
      let typingRef;
      if (selectedChat.isGroup) {
        typingRef = ref(db, `groupChats/${selectedChat.groupId}/userTyping`);
      } else {
        typingRef = ref(
          db,
          `userchatbox/${selectedChat.userId}/${selectedChat.chatId}/userTyping`,
        );
      }
      unsubscribeTyping = onValue(typingRef, (snapshot) => {
        setUserIsTyping(snapshot.val() || false);
      });
    }
    return () => {
      if (unsubscribeTyping) unsubscribeTyping();
    };
  }, [selectedChat]);

  useEffect(() => {
    const savedTemplates = localStorage.getItem("supplierTemplates");
    if (savedTemplates) {
      setTemplates(JSON.parse(savedTemplates));
    } else {
      const defaultTemplates = [
        {
          id: 1,
          title: "Welcome Message",
          message:
            "Hello! Thank you for contacting us. How can we help you today?",
        },
        {
          id: 2,
          title: "Order Confirmation",
          message:
            "Your order has been confirmed and will be processed within 24 hours.",
        },
        {
          id: 3,
          title: "Shipping Update",
          message:
            "Your order has been shipped and you should receive it within 3-5 business days.",
        },
        {
          id: 4,
          title: "Thank You",
          message:
            "Thank you for your business! We appreciate your trust in our services.",
        },
      ];
      setTemplates(defaultTemplates);
      localStorage.setItem(
        "supplierTemplates",
        JSON.stringify(defaultTemplates),
      );
    }
  }, []);

  useEffect(() => {
    if (templates.length > 0) {
      localStorage.setItem("supplierTemplates", JSON.stringify(templates));
    }
  }, [templates]);

  const toggleBulkMode = () => {
    setBulkMode(!bulkMode);
    setSelectedChats([]);
    setSelectedChat(null);
    setShowBulkMessage(false);
  };

  const toggleChatSelection = (chat) => {
    setSelectedChats((prev) => {
      const isSelected = prev.some((c) => c.uniqueId === chat.uniqueId);
      return isSelected
        ? prev.filter((c) => c.uniqueId !== chat.uniqueId)
        : [...prev, chat];
    });
  };

  const handleCreateGroup = async () => {
    if (!newGroupName.trim() || selectedGroupUsers.length === 0) return;

    try {
      // Append "(Group)" automatically if not already included
      let finalGroupName = newGroupName.trim();
      if (!finalGroupName.toLowerCase().endsWith("(group)")) {
        finalGroupName = `${finalGroupName} (Group)`;
      }

      const groupRef = ref(db, `groupChats`);
      const newGroupRef = push(groupRef);

      await set(newGroupRef, {
        groupName: finalGroupName, // Use finalGroupName here
        members: [...selectedGroupUsers.map((user) => user.userId), "supplier"],
        groupAdmin: "supplier",
        messages: {},
      });

      // Reset fields
      setNewGroupName("");
      setSelectedGroupUsers([]);
      setShowCreateGroup(false);
    } catch (error) {
      console.error("Error creating group chat:", error);
      alert("Error creating group chat. Please try again.");
    }
  };

  const handleAddMembers = async () => {
    if (selectedMembersToAdd.length === 0) return;

    try {
      const existingMembers = selectedGroupProfile.members || [];

      const cleanNewMembers = selectedMembersToAdd.filter(
        (member) =>
          member && member !== "supplier" && !existingMembers.includes(member),
      );

      if (cleanNewMembers.length === 0) {
        alert("Selected members already exist in group.");
        return;
      }

      const updatedMembers = [...existingMembers, ...cleanNewMembers];

      await set(
        ref(db, `groupChats/${selectedGroupProfile.groupId}/members`),
        updatedMembers,
      );

      setShowAddMember(false);
      setSelectedMembersToAdd([]);

      setSelectedGroupProfile((prev) => ({
        ...prev,
        members: updatedMembers,
      }));
    } catch (error) {
      console.error("Error adding members:", error);
      alert("Error adding members.");
    }
  };

  const handleRemoveMembers = async () => {
    if (selectedMembersToRemove.length === 0) return;
    try {
      const updatedMembers = selectedGroupProfile.members.filter(
        (m) => !selectedMembersToRemove.includes(m),
      );
      await set(
        ref(db, `groupChats/${selectedGroupProfile.groupId}/members`),
        updatedMembers,
      );
      setShowRemoveMember(false);
      setSelectedMembersToRemove([]);
      setSelectedGroupProfile((prev) => ({ ...prev, members: updatedMembers }));
    } catch (error) {
      console.error("Error removing members:", error);
      alert("Error removing members. Please try again.");
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (
      window.confirm(
        "Are you sure you want to delete this group? This action cannot be undone.",
      )
    ) {
      try {
        await set(ref(db, `groupChats/${groupId}`), null);
        setSelectedChat(null);
      } catch (error) {
        console.error("Error deleting group:", error);
        alert("Error deleting group. Please try again.");
      }
    }
  };

  const handleExitGroup = async (groupId) => {
    if (window.confirm("Are you sure you want to exit this group?")) {
      try {
        const groupRef = ref(db, `groupChats/${groupId}`);
        const snapshot = await new Promise((resolve) =>
          onValue(groupRef, resolve, { onlyOnce: true }),
        );
        const groupData = snapshot.val();

        if (groupData && groupData.members) {
          // Remove current user (supplier) from members
          const updatedMembers = groupData.members.filter(
            (member) => member !== "supplier",
          );

          // If no members left, delete the group
          if (updatedMembers.length === 0) {
            if (
              window.confirm(
                "You are the only member. Exiting will delete the group.",
              )
            ) {
              await set(groupRef, null);
              setSelectedChat(null);
            }
            return;
          }

          // Update members without changing groupAdmin
          await update(groupRef, { members: updatedMembers });

          // Do NOT assign new admin
          // Admin who left will no longer have access

          setSelectedChat(null);
        }
      } catch (error) {
        console.error("Error exiting group:", error);
        alert("Error exiting group. Please try again.");
      }
    }
  };

  const handleSendBulkMessage = async () => {
    if (
      (!bulkMessage.trim() && bulkFiles.length === 0) ||
      selectedChats.length === 0
    )
      return;
    setIsSendingBulk(true);
    const newTimestamp = Date.now();
    try {
      const fileData = [];
      if (bulkFiles.length > 0) {
        for (const file of bulkFiles) {
          const uploadedFile = await uploadFile(
            file,
            selectedChats[0],
            "supplier",
          );
          fileData.push({
            id: uploadedFile.id,
            url: uploadedFile.url,
            name: uploadedFile.name,
            type: uploadedFile.type,
            size: uploadedFile.size,
            uploadedBy: "supplier",
            uploadedAt: Date.now(),
          });
        }
      }
      const promises = selectedChats.map(async (chat) => {
        try {
          if (chat.isGroup) {
            const chatRef = ref(db, `groupChats/${chat.groupId}/messages`);
            const newReply = {
              sender: "supplier",
              message: bulkMessage,
              reply: "",
              replyToId: null,
              timestamp: newTimestamp,

              files: fileData.length > 0 ? fileData : null,
            };
            return push(chatRef, newReply);
          } else {
            const chatRef = ref(
              db,
              `userchatbox/${chat.userId}/${chat.chatId}`,
            );
            const newReply = {
              sender: "supplier",
              message: bulkMessage,
              reply: "",
              replyToId: null,
              timestamp: newTimestamp,
              read: false,
              files: fileData.length > 0 ? fileData : null,
            };
            return push(chatRef, newReply);
          }
        } catch (error) {
          console.error(
            `Error sending message to chat ${chat.uniqueId}:`,
            error,
          );
          throw error;
        }
      });
      await Promise.all(promises);
      setBulkMessage("");
      setBulkFiles([]);
      setSelectedChats([]);
      setShowBulkMessage(false);
      setBulkMode(false);
      alert(`Message sent to ${selectedChats.length} chats successfully!`);
    } catch (error) {
      console.error("Error sending bulk message:", error);
      alert("Error sending bulk message. Please try again.");
    } finally {
      setIsSendingBulk(false);
    }
  };

  const scrollToBottom = useCallback((smooth = true) => {
    if (messagesContainerRef.current) {
      const container = messagesContainerRef.current;
      container.scrollTo({
        top: container.scrollHeight,
        behavior: smooth ? "smooth" : "instant",
      });
    }
  }, []);

  const handleScrollToMessage = useCallback((msgId) => {
    const msgElement = document.getElementById(`msg-${msgId}`);
    if (msgElement) {
      msgElement.scrollIntoView({ behavior: "smooth", block: "center" });
      msgElement.classList.add("sc-highlight");
      setTimeout(() => msgElement.classList.remove("sc-highlight"), 2000);
    }
  }, []);

  const handleCreateTemplate = () => {
    if (newTemplate.title.trim() && newTemplate.message.trim()) {
      const template = {
        id: Date.now(),
        title: newTemplate.title.trim(),
        message: newTemplate.message.trim(),
      };
      setTemplates((prev) => [...prev, template]);
      setNewTemplate({ title: "", message: "" });
      setShowCreateTemplate(false);
    }
  };

  const handleDeleteTemplate = (templateId) => {
    setTemplates((prev) => prev.filter((t) => t.id !== templateId));
  };

  const handleUseTemplate = (template) => {
    if (showBulkMessage) {
      setBulkMessage(template.message);
    } else {
      setReplyMessage(template.message);
    }
    setShowTemplates(false);
    if (showBulkMessage) {
      // Focus on bulk message input if available
    } else {
      inputRef.current?.focus();
    }
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setShowCreateTemplate(true);
    setShowTemplates(false);
  };

  const handleSaveTemplate = () => {
    if (editingTemplate) {
      setTemplates((prev) =>
        prev.map((t) => (t.id === editingTemplate.id ? editingTemplate : t)),
      );
      setEditingTemplate(null);
    } else {
      handleCreateTemplate();
    }
    setShowCreateTemplate(false);
  };

  const validateFile = (file) => {
    try {
      return FileStorage.validateFile(file);
    } catch (error) {
      alert(error.message);
      return false;
    }
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    const validFiles = files.filter(validateFile);
    if (validFiles.length > 0) {
      setSelectedFiles((prev) => [...prev, ...validFiles]);
      setShowFilePreview(true);
    }
    event.target.value = "";
  };

  const handleBulkFileSelect = (event) => {
    const files = Array.from(event.target.files);
    const validFiles = files.filter(validateFile);
    if (validFiles.length > 0) {
      setBulkFiles((prev) => [...prev, ...validFiles]);
    }
    event.target.value = "";
  };

  const handlePaste = (e) => {
    const items = e.clipboardData.items;
    const files = [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind === "file") {
        const file = item.getAsFile();
        if (validateFile(file)) {
          files.push(file);
        }
      }
    }
    if (files.length > 0) {
      setSelectedFiles((prev) => [...prev, ...files]);
      setShowFilePreview(true);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = Array.from(e.dataTransfer.files).filter(validateFile);
    if (files.length > 0) {
      setSelectedFiles((prev) => [...prev, ...files]);
      setShowFilePreview(true);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const removeFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    if (selectedFiles.length === 1) {
      setShowFilePreview(false);
    }
  };

  const removeBulkFile = (index) => {
    setBulkFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const uploadFile = async (file, chat) => {
    try {
      const fileData = await FileStorage.uploadFile(file, chat, "supplier");
      return fileData;
    } catch (error) {
      console.error("Error uploading file:", error);
      throw error;
    }
  };

  const handleClearChat = async (chat) => {
    if (
      window.confirm(
        "Are you sure you want to clear this chat? This action cannot be undone.",
      )
    ) {
      try {
        if (chat.isGroup) {
          const messagesRef = ref(db, `groupChats/${chat.groupId}/messages`);
          await set(messagesRef, {});
        } else {
          const chatRef = ref(db, `userchatbox/${chat.userId}/${chat.chatId}`);
          await set(chatRef, {
            companyName: chat.companyName,
            chatStatus: chat.chatStatus,
          });
        }
      } catch (error) {
        console.error("Error clearing chat:", error);
        alert("Error clearing chat. Please try again.");
      }
    }
  };

  const showUserProfileModal = async (userId) => {
    const profile = await fetchUserProfile(userId);

    setSelectedUserProfile({
      ...(profile || {}),
      userId,
    });

    setShowUserProfile(true);
  };

  const showGroupProfileModal = (group) => {
    setSelectedGroupProfile(group);
    setShowGroupProfile(true);
  };

  const handleScroll = useCallback(() => {
    if (messagesContainerRef.current) {
      const container = messagesContainerRef.current;
      const isNearBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight <
        100;
      setShowScrollButton(!isNearBottom);
    }
  }, []);

  useEffect(() => {
    if (selectedChat?.messages) {
      setTimeout(() => {
        scrollToBottom(selectedChat.messages.length > 0);
      }, 50);
    }
  }, [selectedChat, scrollToBottom]);

  const markMessagesAsRead = useCallback(async (chat) => {
    if (!chat || !auth.currentUser?.uid) return;

    const supplierUid = auth.currentUser.uid;
    const updates = {}; // ← Define updates here (at the top)

    if (chat.isGroup) {
      // Group Chat: Use readBy array
      chat.messages.forEach((msg) => {
        if (
          msg.sender !== "supplier" &&
          !(msg.readBy || []).includes(supplierUid)
        ) {
          const currentReadBy = msg.readBy || [];
          updates[`groupChats/${chat.groupId}/messages/${msg.id}/readBy`] = [
            ...currentReadBy,
            supplierUid,
          ];
        }
      });
    } else {
      // Individual Chat: Also use readBy array (Recommended)
      chat.messages.forEach((msg) => {
        if (
          msg.sender === "user" &&
          !(msg.readBy || []).includes(supplierUid)
        ) {
          const currentReadBy = msg.readBy || [];
          updates[
            `userchatbox/${chat.userId}/${chat.chatId}/${msg.id}/readBy`
          ] = [...currentReadBy, supplierUid];
        }
      });
    }

    // Only update if there are changes
    if (Object.keys(updates).length > 0) {
      try {
        await update(ref(db), updates);

        // Optional: Keep lastReadBySupplier for quick unread count (backward compatibility)
        if (!chat.isGroup) {
          const now = Date.now();
          await update(ref(db, `userchatbox/${chat.userId}/${chat.chatId}`), {
            lastReadBySupplier: now,
          });
        }
      } catch (error) {
        console.error("Error marking messages as read:", error);
      }
    }
  }, []);

  useEffect(() => {
    if (selectedChat) {
      markMessagesAsRead(selectedChat);
    }
  }, [selectedChat, markMessagesAsRead]);

  const handleChatSelect = async (chat) => {
    if (bulkMode) {
      toggleChatSelection(chat);
      return;
    }

    if (chat.isSidebarOnly) {
      try {
        const newChatId = `chat_${chat.userId}`;

        const userChatsRef = ref(db, `userchatbox/${chat.userId}`);

        const snapshot = await new Promise((resolve) =>
          onValue(userChatsRef, resolve, { onlyOnce: true }),
        );

        const data = snapshot.val();

        // find latest chat or create chat-1
        let chatId = "chat-1";

        if (data) {
          const keys = Object.keys(data).filter((k) => k.startsWith("chat-"));

          if (keys.length > 0) {
            chatId = keys.sort((a, b) => {
              const aTime = data[a]?.createdAt || 0;
              const bTime = data[b]?.createdAt || 0;
              return bTime - aTime;
            })[0];
          }
        } else {
          // create first chat
          await set(ref(db, `userchatbox/${chat.userId}/chat-1`), {
            companyName: chat.companyName || "User",
            createdAt: Date.now(),
            chatStatus: { status: "open" },
          });
        }
        const newChatObject = {
          ...chat,
          chatId,
          uniqueId: `${chat.userId}-${newChatId}`,
          isSidebarOnly: false,
        };

        setSelectedChat(newChatObject);
      } catch (error) {
        console.error("Error opening chat:", error);
        alert("Failed to open chat.");
      }
    } else {
      setSelectedChat(chat);
    }

    setShowCreateGroup(false);
    setShowChatMenu(false);
    setShowSearch(false);
    setReplyingTo(null);

    markMessagesAsRead(chat);
  };

  const handleReplyToMessage = (message) => {
    setReplyingTo(message);
    inputRef.current?.focus();
  };

  const clearReply = () => {
    setReplyingTo(null);
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  const handleSendReply = useCallback(async () => {
    if (
      isSending ||
      (!replyMessage.trim() && selectedFiles.length === 0 && !isEditing) ||
      !selectedChat
    )
      return;

    setIsSending(true);
    const newTimestamp = Date.now();

    try {
      // ========== EDIT MODE ==========
      if (isEditing && editingMessageId) {
        let msgRef;
        if (selectedChat.isGroup) {
          msgRef = ref(
            db,
            `groupChats/${selectedChat.groupId}/messages/${editingMessageId}`,
          );
        } else {
          msgRef = ref(
            db,
            `userchatbox/${selectedChat.userId}/${selectedChat.chatId}/${editingMessageId}`,
          );
        }

        await update(msgRef, {
          message: replyMessage,
          editedAt: Date.now(),
        });

        // Optimistic local update
        setSelectedChat((prev) => ({
          ...prev,
          messages: prev.messages.map((m) =>
            m.id === editingMessageId
              ? { ...m, message: replyMessage, editedAt: Date.now() }
              : m,
          ),
        }));

        // Reset edit state
        setIsEditing(false);
        setEditingMessageId(null);
        setReplyMessage("");

        if (inputRef.current) {
          inputRef.current.style.height = "42px";
        }

        setIsSending(false);
        return;
      }

      // ========== NORMAL SEND MODE ==========
      const tempId = `temp-${newTimestamp}`;

      let newReply = {
        sender: "supplier",
        message: replyMessage,
        reply: replyingTo ? getRepliedText(replyingTo) : "",
        replyToId: replyingTo?.id || null,
        timestamp: newTimestamp,
        read: false,
        files: null,
      };

      // Optimistic UI update
      setSelectedChat((prev) => ({
        ...prev,
        messages: [
          ...prev.messages,
          { id: tempId, ...newReply, isSending: true },
        ].sort((a, b) => Number(a.timestamp) - Number(b.timestamp)),
      }));

      let chatRef;
      const fileData = [];

      if (selectedFiles.length > 0) {
        for (const file of selectedFiles) {
          if (!file.id) {
            setUploadProgress((prev) => ({
              ...prev,
              [file.name]: { progress: 0, status: "uploading" },
            }));

            try {
              const uploadedFile = await uploadFile(file, selectedChat);
              fileData.push({
                id: uploadedFile.id,
                url: uploadedFile.url,
                name: uploadedFile.name,
                type: uploadedFile.type,
                size: uploadedFile.size,
                uploadedBy: "supplier",
                uploadedAt: uploadedFile.uploadedAt,
              });

              setUploadProgress((prev) => ({
                ...prev,
                [file.name]: { progress: 100, status: "completed" },
              }));
            } catch (fileError) {
              console.error("File upload failed:", fileError);
              setUploadProgress((prev) => ({
                ...prev,
                [file.name]: { status: "failed" },
              }));
              throw new Error("Failed to upload file.");
            }
          } else {
            fileData.push(file);
          }
        }
      }

      newReply.files = fileData.length > 0 ? fileData : null;

      if (selectedChat.isGroup) {
        chatRef = ref(db, `groupChats/${selectedChat.groupId}/messages`);
      } else {
        chatRef = ref(
          db,
          `userchatbox/${selectedChat.userId}/${selectedChat.chatId}`,
        );
      }

      const newRef = await push(chatRef, newReply);
      const newId = newRef.key;

      // Replace temp message with real one
      setSelectedChat((prev) => {
        const updatedMessages = prev.messages.map((msg) =>
          msg.id === tempId
            ? { ...msg, id: newId, files: fileData, isSending: false }
            : msg,
        );
        return {
          ...prev,
          messages: updatedMessages,
        };
      });

      // Reset states
      setReplyMessage("");
      setSelectedFiles([]);
      if (inputRef.current) {
        inputRef.current.style.height = "42px";
      }
      setShowFilePreview(false);
      setUploadProgress({});
      setIsTyping(false);
      setEditingTemplate(null);
      setReplyingTo(null);

      setTimeout(() => {
        scrollToBottom();
      }, 100);
    } catch (error) {
      console.error("Error sending reply:", error);
      alert(
        "Failed to send message. Please check your connection or try again.",
      );

      // Remove the temporary message on error
      setSelectedChat((prev) => ({
        ...prev,
        messages: prev.messages.filter((msg) => !msg.id?.startsWith("temp-")),
      }));
      setUploadProgress({});
    } finally {
      setIsSending(false);
    }
  }, [
    replyMessage,
    selectedChat,
    selectedFiles,
    scrollToBottom,
    isSending,
    replyingTo,
    isEditing,
    editingMessageId,
  ]);
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSendReply();
      }
    },
    [handleSendReply],
  );

  const handleInputChange = useCallback((e) => {
    const newValue = e.target.value;

    setReplyMessage(newValue);

    const typing = newValue.length > 0;
    setIsTyping(typing);

    // Reset first so shrinking also works
    e.target.style.height = "42px";

    // Expand based on content
    e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
  }, []);

  const getFormattedDate = (timestamp) => {
    const date = new Date(Number(timestamp));
    const now = new Date();
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    if (date.toDateString() === now.toDateString()) {
      return "Today";
    } else if (date.toDateString() === yesterday.toDateString()) {
      return "Yesterday";
    } else {
      return date.toLocaleDateString([], {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(Number(timestamp));
    if (isNaN(date.getTime())) {
      return "";
    }
    const now = new Date();
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    const timeString = date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
    if (date.toDateString() === now.toDateString()) {
      return timeString;
    } else if (date.toDateString() === yesterday.toDateString()) {
      return `Yesterday ${timeString}`;
    } else {
      return `${date.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" })} ${timeString}`;
    }
  };

  const getMessageStatus = (message) => {
    if (message.sender !== "supplier") return null;

    const isRead = Array.isArray(message.readBy) && message.readBy.length > 0;

    if (!isRead) {
      return <span className="sc-message-status sc-sent">✓</span>;
    }

    return (
      <span className="sc-message-status sc-read">
        <span className="sc-tick">✓</span>
        <span className="sc-tick sc-tick-second">✓</span>
      </span>
    );
  };

  const linkify = (text) => {
    if (!text) return text;

    const urlRegex = /((?:https?:\/\/|www\.)[^\s<]+)/gi;

    return text.split(urlRegex).map((part, index) => {
      if (!part.match(urlRegex)) {
        return part;
      }

      const trailingMatch = part.match(/[.,!?;:)]*$/);
      const trailing = trailingMatch ? trailingMatch[0] : "";
      const url = trailing ? part.slice(0, -trailing.length) : part;
      const href = /^https?:\/\//i.test(url) ? url : `https://${url}`;

      return (
        <React.Fragment key={index}>
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="sc-message-link"
          >
            {url}
          </a>
          {trailing}
        </React.Fragment>
      );
    });
  };

  const getRepliedText = (replyTo) => {
    const content = replyTo.message || replyTo.reply || "";
    if (content)
      return content.substring(0, 50) + (content.length > 50 ? "..." : "");
    if (replyTo.files && replyTo.files.length > 0) {
      const file = replyTo.files[0];
      if (file.type.startsWith("image/"))
        return `Image: ${file.name || "unnamed"}`;
      if (file.type === "application/pdf")
        return `PDF: ${file.name || "unnamed"}`;
      return `File: ${file.name || "unnamed"}`;
    }
    return "Message";
  };

  const handleReplyClick = () => {
    if (replyingTo && replyingTo.id) {
      const msgElement = document.getElementById(`msg-${replyingTo.id}`);
      if (msgElement) {
        msgElement.scrollIntoView({ behavior: "smooth", block: "center" });
        msgElement.classList.add("sc-highlight");
        setTimeout(() => msgElement.classList.remove("sc-highlight"), 2000);
      }
    }
  };

  const renderFileAttachment = (file) => {
    const isImage = file.type.startsWith("image/");
    return (
      <div
        key={file.id || file.url}
        className="sc-file-attachment"
        onClick={() => window.open(file.url, "_blank")}
        style={{ cursor: "pointer" }}
      >
        {isImage ? (
          <img
            src={file.url || "/placeholder.svg"}
            alt={file.name}
            className="sc-attached-image"
          />
        ) : (
          <div className="sc-attached-file">
            <IoDocumentOutline className="sc-file-icon" />
            <span className="sc-file-name">{file.name}</span>
            <span className="sc-file-size">
              ({(file.size / 1024 / 1024).toFixed(2)} MB)
            </span>
          </div>
        )}
      </div>
    );
  };

  const existingUserIds = new Set(chats.map((chat) => chat.userId));

  const sidebarOnlyUsers = allSidebarUsers.filter(
    (user) => !existingUserIds.has(user.userId),
  );

  const sortedChats = [...groupChats, ...chats, ...sidebarOnlyUsers].sort(
    (a, b) => {
      const aTime = a.lastMessage?.timestamp
        ? Number(a.lastMessage.timestamp)
        : 0;

      const bTime = b.lastMessage?.timestamp
        ? Number(b.lastMessage.timestamp)
        : 0;

      return bTime - aTime;
    },
  );

  const showCreateGroupButton = !selectedChat && !bulkMode;

  const handleTouchStart = (e, msg) => {
    const touch = e.touches[0];
    setSwipeState({
      startX: touch.clientX,
      currentX: touch.clientX,
      isDragging: true,
      messageId: msg.id || msg.timestamp,
    });
  };

  const handleTouchMove = (e, msg) => {
    if (
      !swipeState.isDragging ||
      swipeState.messageId !== (msg.id || msg.timestamp)
    )
      return;
    const touch = e.touches[0];
    const deltaX = touch.clientX - swipeState.startX;
    if (deltaX < 0 && Math.abs(deltaX) <= 100) {
      setSwipeState((prev) => ({
        ...prev,
        currentX: touch.clientX,
      }));
    }
  };

  const handleTouchEnd = (e, msg) => {
    if (
      !swipeState.isDragging ||
      swipeState.messageId !== (msg.id || msg.timestamp)
    )
      return;
    const deltaX = swipeState.currentX - swipeState.startX;
    if (deltaX < -50) {
      handleReplyToMessage(msg);
    }
    setSwipeState({
      startX: 0,
      currentX: 0,
      isDragging: false,
      messageId: null,
    });
  };

  const getTimestampMs = (timestamp) => {
    let ts =
      typeof timestamp === "number"
        ? timestamp
        : Number.parseInt(timestamp, 10);
    if (isNaN(ts)) return 0;
    if (ts < 1e12) ts *= 1000;
    return ts;
  };

  const handleEditMessage = useCallback(
    (msgId, currentText) => {
      const msg = selectedChat?.messages.find((m) => m.id === msgId);
      if (!msg) return;

      // 15-minute limit (same as user side)
      const timeDiff = Date.now() - getTimestampMs(msg.timestamp);
      if (timeDiff > 15 * 60 * 1000) {
        alert("You can only edit messages within 15 minutes of sending.");
        return;
      }

      setReplyMessage(currentText || "");
      setIsEditing(true);
      setEditingMessageId(msgId);
      inputRef.current?.focus();
    },
    [selectedChat],
  );

  const handleDeleteForEveryone = useCallback(async () => {
    if (!deleteMsg || !selectedChat) return;

    try {
      let msgRef;
      if (selectedChat.isGroup) {
        msgRef = ref(
          db,
          `groupChats/${selectedChat.groupId}/messages/${deleteMsg.id}`,
        );
      } else {
        msgRef = ref(
          db,
          `userchatbox/${selectedChat.userId}/${selectedChat.chatId}/${deleteMsg.id}`,
        );
      }

      await update(msgRef, {
        isDeleted: true,
        message: "This message was deleted",
        files: null,
        reply: "",
      });

      // Optimistic local update
      setSelectedChat((prev) => {
        const newMessages = prev.messages.map((m) =>
          m.id === deleteMsg.id
            ? {
                ...m,
                isDeleted: true,
                message: "This message was deleted",
                files: null,
                reply: "",
              }
            : m,
        );
        return { ...prev, messages: newMessages };
      });

      setDeleteModalVisible(false);
      setDeleteMsg(null);
    } catch (error) {
      console.error("Error deleting message for everyone:", error);
      alert("Error deleting message. Please try again.");
    }
  }, [deleteMsg, selectedChat]);

  const handleDeleteForMe = useCallback(async () => {
    if (!deleteMsg || !selectedChat || !auth.currentUser) return;

    const currentId = auth.currentUser.uid; // or "supplier" if you use that consistently

    try {
      let msgRef;
      if (selectedChat.isGroup) {
        msgRef = ref(
          db,
          `groupChats/${selectedChat.groupId}/messages/${deleteMsg.id}`,
        );
      } else {
        msgRef = ref(
          db,
          `userchatbox/${selectedChat.userId}/${selectedChat.chatId}/${deleteMsg.id}`,
        );
      }

      const deletedBy = deleteMsg.deletedBy || [];
      if (!deletedBy.includes(currentId) && !deletedBy.includes("supplier")) {
        deletedBy.push(currentId); // or "supplier"
      }
      await update(msgRef, { deletedBy });

      setSelectedChat((prev) => {
        const newMessages = prev.messages.map((m) =>
          m.id === deleteMsg.id
            ? { ...m, deletedBy: [...(m.deletedBy || []), currentId] }
            : m,
        );
        return { ...prev, messages: newMessages };
      });

      setDeleteModalVisible(false);
      setDeleteMsg(null);
    } catch (error) {
      console.error("Error deleting message for me:", error);
      alert("Error deleting message. Please try again.");
    }
  }, [deleteMsg, selectedChat]);

  const handleMessageAction = useCallback(
    (action, msg = null) => {
      const target = msg;
      if (!target) return;

      switch (action) {
        case "copy":
          let text = target.message || target.reply || "";
          if (!text && target.files?.length > 0) {
            text = `Attachment: ${target.files[0].name || "file"}`;
          }
          if (text) {
            navigator.clipboard
              .writeText(text.trim())
              .then(() => alert("Copied!"))
              .catch(() => alert("Copy failed"));
          }
          break;

        case "reply":
          handleReplyToMessage(target);
          break;

        case "edit":
          if (target.files && target.files.length > 0) {
            alert("Messages with files cannot be edited.");
            break;
          }
          handleEditMessage(target.id, target.message || "");
          break;

        case "delete":
          setDeleteMsg(target);
          setDeleteModalVisible(true);
          break;

        default:
          break;
      }

      setMenuMessageId(null);
    },
    [handleReplyToMessage, handleEditMessage],
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (chatMenuRef.current && !chatMenuRef.current.contains(event.target)) {
        setShowChatMenu(false);
      }
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearch(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [chatMenuRef, searchRef]);

  return (
    <div className="sc-chatbot-container">
      <div className="sc-chat-sidebar">
        <div className="sc-sidebar-header">
          <div className="sc-sidebar-title">
            <h1>Messages</h1>
            <button
              className={`sc-bulk-mode-btn ${bulkMode ? "active" : ""}`}
              onClick={toggleBulkMode}
              title={bulkMode ? "Exit bulk mode" : "Enter bulk mode"}
            >
              {bulkMode ? <IoCheckbox /> : <IoCheckboxOutline />}
            </button>
            <button
              className="sc-create-group-btn"
              onClick={() => setShowCreateGroup(true)}
              title="Create group chat"
            >
              <IoLayersOutline />
            </button>
          </div>
          {bulkMode && selectedChats.length > 0 && (
            <div className="sc-bulk-counter">
              {selectedChats.length} chat{selectedChats.length !== 1 ? "s" : ""}{" "}
              selected
              <button
                className="sc-bulk-send-btn"
                onClick={() => setShowBulkMessage(true)}
                disabled={selectedChats.length === 0}
              >
                Send Message
              </button>
            </div>
          )}
        </div>
        <div className="sc-chat-list">
          {sortedChats.length === 0 ? (
            <div className="sc-empty-chat">
              <div className="sc-empty-icon">💬</div>
              <p className="sc-empty-title">No conversations yet</p>
              <p className="sc-empty-subtitle">
                Messages from user will appear here
              </p>
            </div>
          ) : (
            sortedChats.map((chat) => (
              <div
                key={chat.uniqueId}
                onClick={() => handleChatSelect(chat)}
                className={`sc-chat-item ${
                  bulkMode
                    ? selectedChats.some((c) => c.uniqueId === chat.uniqueId)
                      ? "sc-selected"
                      : ""
                    : selectedChat?.uniqueId === chat.uniqueId
                      ? "sc-active"
                      : ""
                }`}
              >
                {bulkMode && (
                  <div className="sc-chat-checkbox">
                    {selectedChats.some((c) => c.uniqueId === chat.uniqueId) ? (
                      <IoCheckbox className="sc-checkbox-checked" />
                    ) : (
                      <IoCheckboxOutline className="sc-checkbox-unchecked" />
                    )}
                  </div>
                )}
                <div className="sc-chat-avatar">
                  <div className="sc-avatar-circle">
                    {chat.isGroup
                      ? "G"
                      : (chat.companyName.charAt(0) || "U").toUpperCase()}
                  </div>
                  {chat.unreadCount > 0 &&
                    selectedChat?.uniqueId !== chat.uniqueId && (
                      <div className="sc-unread-indicator"></div>
                    )}
                </div>
                <div className="sc-chat-info">
                  <div className="sc-chat-header">
                    <h3 className="sc-company-name">
                      {chat.isGroup ? `${chat.groupName}` : chat.companyName}
                    </h3>
                    <div className="sc-chat-meta">
                      {!chat.isGroup &&
                        (() => {
                          const sidebarUser = allSidebarUsers.find(
                            (user) => user.userId === chat.userId,
                          );

                          const role = String(
                            chat.userType || sidebarUser?.userType || "",
                          )
                            .trim()
                            .toLowerCase();

                          return role ? (
                            <span
                              className={`sc-user-type-tag sc-user-type-${role.replace(
                                /[^a-z0-9_-]/g,
                                "",
                              )}`}
                            >
                              {role}
                            </span>
                          ) : null;
                        })()}
                      {chat.lastMessage && (
                        <span className="sc-message-time-stamp">
                          {formatTime(chat.lastMessage.timestamp)}
                        </span>
                      )}
                      {chat.unreadCount > 0 &&
                        selectedChat?.uniqueId !== chat.uniqueId && (
                          <span className="sc-unread-badge">
                            {chat.unreadCount}
                          </span>
                        )}
                    </div>
                  </div>
                  <div className="sc-chat-preview">
                    <p className="sc-last-message">
                      {chat.lastMessage ? (
                        <>
                          {chat.lastMessage.sender === "supplier"
                            ? "You: "
                            : ""}
                          {chat.lastMessage.files
                            ? "📎 File attachment"
                            : chat.lastMessage.reply ||
                              chat.lastMessage.message ||
                              "No messages yet"}
                        </>
                      ) : (
                        "No messages yet"
                      )}
                    </p>
                  </div>
                  <div className="sc-chat-footer">
                    {!chat.isGroup && (
                      <>
                        <span
                          className={`sc-chat-status ${chat.chatStatus.status}`}
                        >
                          {chat.chatStatus.status}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="sc-chat-main">
        {selectedChat && !bulkMode ? (
          <>
            <button
              className={`sc-scroll-to-bottom ${showScrollButton ? "" : "hidden"}`}
              onClick={() => scrollToBottom(true)}
            ></button>

            <div className="sc-chat-header-main">
              <div className="sc-header-info">
                <div
                  className="sc-chat-avatar"
                  onClick={() => {
                    if (selectedChat.isGroup) {
                      showGroupProfileModal(selectedChat);
                    } else {
                      showUserProfileModal(selectedChat.userId);
                    }
                  }}
                >
                  <div className="sc-avatar-circle">
                    {selectedChat.isGroup
                      ? "G"
                      : (
                          selectedChat.companyName.charAt(0) || "U"
                        ).toUpperCase()}
                  </div>
                </div>
                <div className="sc-header-text">
                  <h2>
                    {selectedChat.isGroup
                      ? `${selectedChat.groupName}`
                      : selectedChat.companyName}
                  </h2>
                </div>
              </div>
              <div className="sc-header-actions">
                <button
                  className="sc-action-btn"
                  onClick={() => {
                    setShowSearch(!showSearch);
                    if (showSearch) setSearchQuery("");
                  }}
                  title="Search"
                >
                  <IoSearchOutline />
                </button>
                <button
                  className="sc-action-btn"
                  onClick={() => setShowChatMenu(!showChatMenu)}
                  title="Menu"
                >
                  <IoEllipsisVerticalOutline />
                </button>
                {showChatMenu && (
                  <div className="sc-chat-menu-dropdown" ref={chatMenuRef}>
                    <button onClick={() => handleClearChat(selectedChat)}>
                      <IoTrashBinOutline /> Clear Chat
                    </button>
                    {selectedChat.isGroup && (
                      <>
                        <button
                          onClick={() => handleExitGroup(selectedChat.groupId)}
                        >
                          <IoExitOutline /> Exit Group
                        </button>
                        <button
                          onClick={() =>
                            handleDeleteGroup(selectedChat.groupId)
                          }
                        >
                          <IoTrashBinOutline /> Delete Group
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>

            {showSearch && (
              <div className="sc-search-bar" ref={searchRef}>
                <input
                  type="text"
                  placeholder="Search messages..."
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="sc-search-input"
                />
              </div>
            )}

            <div
              className="sc-messages-container"
              ref={messagesContainerRef}
              onScroll={handleScroll}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
            >
              {userIsTyping && (
                <div className="sc-typing-indicator">User is typing...</div>
              )}

              {selectedChat.messages.length === 0 ? (
                <div className="sc-empty-chat">
                  <div className="sc-empty-icon">💬</div>
                  <p>No messages yet. Start the conversation!</p>
                </div>
              ) : (
                <>
                  {selectedChat.messages
                    .filter((msg) => {
                      // Hide messages deleted for me
                      const currentId = auth.currentUser?.uid || "supplier";
                      const isDeletedForMe =
                        (msg.deletedBy || []).includes(currentId) ||
                        (msg.deletedBy || []).includes("supplier");
                      if (isDeletedForMe) return false;

                      if (!searchQuery.trim()) return true;
                      const messageText = msg.message || msg.reply || "";
                      return messageText
                        .toLowerCase()
                        .includes(searchQuery.toLowerCase());
                    })
                    .map((msg, index, filteredMessages) => {
                      const isUser = selectedChat.isGroup
                        ? msg.sender !== "supplier"
                        : msg.sender === "user";

                      const messageText = msg.message || msg.reply || "";
                      const formattedTime = getMessageTime(msg);
                      const hasContent =
                        messageText.trim() !== "" ||
                        (msg.files && msg.files.length > 0) ||
                        msg.isDeleted;

                      if (!hasContent) return null;

                      const senderName = selectedChat.isGroup
                        ? msg.sender === "supplier"
                          ? "You"
                          : senderDisplayMap[msg.sender] || "Loading..."
                        : "";

                      const isCurrentlyDragging =
                        swipeState.isDragging &&
                        swipeState.messageId === (msg.id || msg.timestamp);
                      const deltaX = isCurrentlyDragging
                        ? swipeState.currentX - swipeState.startX
                        : 0;
                      const swipeDistance = Math.max(-100, Math.min(0, deltaX));
                      const messageStyle = {
                        transform: `translateX(${swipeDistance}px)`,
                        transition: swipeState.isDragging
                          ? "none"
                          : "transform 0.2s ease-out",
                        position: "relative",
                      };

                      const prevMsg =
                        index > 0 ? filteredMessages[index - 1] : null;
                      const currentDate = getFormattedDate(msg.timestamp);
                      const prevDate = prevMsg
                        ? getFormattedDate(prevMsg.timestamp)
                        : null;
                      const showDateHeader = currentDate !== prevDate;

                      let repliedMsg = null;
                      if (msg.replyToId) {
                        repliedMsg = selectedChat.messages.find(
                          (m) => m.id === msg.replyToId,
                        );
                      }
                      let repliedSender = repliedMsg
                        ? repliedMsg.sender
                        : msg.replyTo
                          ? msg.replyTo.sender
                          : null;
                      let repliedContent =
                        msg.reply ||
                        (repliedMsg ? getRepliedText(repliedMsg) : "");
                      const senderPrefix =
                        repliedSender === "supplier"
                          ? "You"
                          : selectedChat.isGroup
                            ? memberProfiles[repliedSender]?.name ||
                              repliedSender
                            : "User";

                      // ===== DELETED MESSAGE =====
                      if (msg.isDeleted) {
                        return (
                          <React.Fragment
                            key={`${msg.id || index}-${msg.timestamp || msg.createdAt}`}
                          >
                            {showDateHeader && (
                              <div className="sc-date-header">
                                <span>{currentDate}</span>
                              </div>
                            )}
                            <div
                              id={`msg-${msg.id}`}
                              className={`sc-message ${isUser ? "sc-supplier" : "sc-user"} sc-deleted`}
                            >
                              <div className="sc-message-content">
                                <p className="deleted">
                                  This message was deleted
                                </p>
                                <div className="sc-message-footer">
                                  {formattedTime && (
                                    <span className="sc-message-time">
                                      {formattedTime}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </React.Fragment>
                        );
                      }

                      // ===== NORMAL MESSAGE =====
                      return (
                        <React.Fragment
                          key={`${msg.id || index}-${msg.timestamp || msg.createdAt}`}
                        >
                          {showDateHeader && (
                            <div className="sc-date-header">
                              <span>{currentDate}</span>
                            </div>
                          )}

                          <div
                            id={`msg-${msg.id}`}
                            className={`sc-message ${isUser ? "sc-supplier" : "sc-user"} ${
                              highlightedMessage === msg.id ? "highlighted" : ""
                            }`}
                            style={messageStyle}
                            onDoubleClick={() => handleReplyToMessage(msg)}
                            onTouchStart={(e) => handleTouchStart(e, msg)}
                            onTouchMove={(e) => handleTouchMove(e, msg)}
                            onTouchEnd={(e) => handleTouchEnd(e, msg)}
                          >
                            {isCurrentlyDragging && swipeDistance < -20 && (
                              <div className="sc-swipe-reply-indicator">
                                <span>Reply</span>
                                <IoArrowUndoOutline />
                              </div>
                            )}

                            {/* ========== THIS IS THE IMPORTANT WRAPPER ========== */}
                            <div className="sc-message-bubble-wrapper">
                              <div className="sc-message-content">
                                {repliedContent && (
                                  <div
                                    className="sc-reply-reference"
                                    onClick={() =>
                                      msg.replyToId &&
                                      handleScrollToMessage(msg.replyToId)
                                    }
                                  >
                                    <IoArrowUndoOutline />
                                    <span>
                                      {senderPrefix}: {repliedContent}
                                    </span>
                                  </div>
                                )}

                                {selectedChat.isGroup && senderName && (
                                  <span className="sender-name">
                                    {senderName}:
                                  </span>
                                )}

                                {messageText && <p>{linkify(messageText)}</p>}

                                {msg.files &&
                                  msg.files.map((file, fileIndex) => (
                                    <div key={`${file.id || fileIndex}`}>
                                      {renderFileAttachment(file)}
                                      {msg.isSending && (
                                        <div className="sc-sending-indicator">
                                          Sending...
                                        </div>
                                      )}
                                    </div>
                                  ))}

                                {(formattedTime || getMessageStatus(msg)) && (
                                  <div className="sc-message-footer">
                                    {formattedTime && (
                                      <span className="sc-message-time">
                                        {formattedTime}
                                        {msg.editedAt && (
                                          <span className="sc-edited-label">
                                            {" "}
                                            (edited)
                                          </span>
                                        )}
                                      </span>
                                    )}
                                    {getMessageStatus(msg)}
                                    {msg.isSending && !msg.files && (
                                      <span className="sc-sending-indicator">
                                        Sending...
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>

                              {/* ===== HOVER ACTIONS (Reply + Menu) ===== */}
                              <div className="sc-message-hover-actions">
                                <button
                                  className="sc-hover-reply-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleReplyToMessage(msg);
                                  }}
                                  title="Reply"
                                >
                                  <IoArrowUndoOutline />
                                </button>

                                <div className="sc-hover-menu-wrapper">
                                  <button
                                    className="sc-hover-menu-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setMenuMessageId(
                                        menuMessageId === msg.id
                                          ? null
                                          : msg.id,
                                      );
                                    }}
                                    title="More"
                                  >
                                    <IoEllipsisVerticalOutline />
                                  </button>

                                  {menuMessageId === msg.id && (
                                    <div className="sc-message-dropdown">
                                      {(msg.message || msg.reply) && (
                                        <button
                                          onClick={() => {
                                            handleMessageAction("copy", msg);
                                            setMenuMessageId(null);
                                          }}
                                        >
                                          <FaCopy /> Copy
                                        </button>
                                      )}

                                      {/* Edit – only own messages, no files, within 15 min */}
                                      {(() => {
                                        const isOwn = msg.sender === "supplier";
                                        const canEdit =
                                          Date.now() -
                                            getTimestampMs(msg.timestamp) <
                                            15 * 60 * 1000 &&
                                          (!msg.files ||
                                            msg.files.length === 0);

                                        return isOwn && canEdit ? (
                                          <button
                                            onClick={() => {
                                              handleMessageAction("edit", msg);
                                              setMenuMessageId(null);
                                            }}
                                          >
                                            <FaEdit /> Edit
                                          </button>
                                        ) : null;
                                      })()}

                                      <button
                                        onClick={() => {
                                          handleMessageAction("delete", msg);
                                          setMenuMessageId(null);
                                        }}
                                      >
                                        <FaTrash /> Delete
                                      </button>

                                      <button
                                        className="sc-action-btn cancel"
                                        onClick={() => setMenuMessageId(null)}
                                      >
                                        <FaTimes /> Cancel
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                            {/* ========== END OF WRAPPER ========== */}
                          </div>
                        </React.Fragment>
                      );
                    })}
                </>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Reply preview */}
            {replyingTo && (
              <div
                className="sc-reply-indicator"
                onClick={handleReplyClick}
                style={{ cursor: "pointer" }}
              >
                <IoArrowUndoOutline />
                <span>Replying to: {getRepliedText(replyingTo)}</span>
                <button onClick={clearReply}>×</button>
              </div>
            )}

            {/* Edit mode indicator */}
            {isEditing && (
              <div className="sc-edit-indicator">
                <FaEdit /> Editing message...
                <button
                  onClick={() => {
                    setIsEditing(false);
                    setEditingMessageId(null);
                    setReplyMessage("");
                  }}
                >
                  Cancel
                </button>
              </div>
            )}

            {showFilePreview && selectedFiles.length > 0 && (
              <div className="sc-file-preview-container">
                <div className="sc-file-preview-header">
                  <span>Selected Files ({selectedFiles.length})</span>
                  <button
                    className="sc-close-preview"
                    onClick={() => setShowFilePreview(false)}
                  >
                    ×
                  </button>
                </div>
                <div className="sc-file-preview-list">
                  {selectedFiles.map((file, index) => (
                    <div key={index} className="sc-file-preview-item">
                      {file.type.startsWith("image/") ? (
                        <img
                          src={URL.createObjectURL(file) || "/placeholder.svg"}
                          alt={file.name}
                          className="sc-preview-image"
                        />
                      ) : (
                        <div className="sc-preview-file">
                          <IoDocumentOutline className="sc-preview-file-icon" />
                        </div>
                      )}
                      <div className="sc-file-info">
                        <div className="sc-file-name">{file.name}</div>
                        <div className="sc-file-size">
                          {(file.size / 1024 / 1024).toFixed(2)} MB
                        </div>
                        {uploadProgress[file.name] && (
                          <div className="sc-upload-status">
                            {uploadProgress[file.name].status ===
                              "uploading" && (
                              <span>
                                Sending... ({uploadProgress[file.name].progress}
                                %)
                              </span>
                            )}
                            {uploadProgress[file.name].status ===
                              "completed" && <span>Uploaded</span>}
                            {uploadProgress[file.name].status === "failed" && (
                              <span>Upload Failed</span>
                            )}
                          </div>
                        )}
                      </div>
                      <button
                        className="sc-remove-file"
                        onClick={() => removeFile(index)}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="sc-input-wrapper" onPaste={handlePaste}>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/png,image/jpg,image/jpeg,application/pdf"
                onChange={handleFileSelect}
                style={{ display: "none" }}
              />
              <button
                className="sc-attach-btn"
                onClick={() => fileInputRef.current?.click()}
                title="Attach files"
              >
                <IoAttachOutline />
              </button>
              <button
                className="sc-templates-btn"
                onClick={() => setShowTemplates((prev) => !prev)}
                title="Message templates"
              >
                <IoDocumentOutline />
              </button>
              <textarea
                ref={inputRef}
                value={replyMessage}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder={
                  isEditing ? "Edit your message..." : "Type a message..."
                }
                className="sc-message-input"
                rows={1}
                style={{
                  resize: "none",
                  overflow: "auto",
                  minHeight: "42px",
                  maxHeight: "120px",
                  padding: "10px 12px",
                }}
              />
              <button
                onClick={handleSendReply}
                disabled={
                  isSending ||
                  (!replyMessage.trim() &&
                    selectedFiles.length === 0 &&
                    !isEditing)
                }
                className="sc-send-btn"
              >
                ➤
              </button>
            </div>
          </>
        ) : (
          <div className="sc-empty-state">
            <div className="sc-empty-content">
              <div className="sc-empty-icon">💬</div>
              <h3>
                {bulkMode ? "Bulk Message Mode" : "Welcome to Supplier Chat"}
              </h3>
              <p>
                {bulkMode
                  ? "Select multiple chats from the sidebar to send bulk messages"
                  : "Select a conversation or group to start messaging"}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* ========== DELETE MODAL ========== */}
      {deleteModalVisible && deleteMsg && (
        <div
          className="delete-sc-modal-overlay"
          onClick={() => setDeleteModalVisible(false)}
        >
          <div
            className="delete-sc-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="delete-sc-modal-header">
              <h3>Delete Message</h3>
              <button onClick={() => setDeleteModalVisible(false)}>
                <IoCloseOutline />
              </button>
            </div>
            <div className="delete-sc-modal-body">
              <button onClick={handleDeleteForMe}>
                <FaTrash /> Delete for me
              </button>

              {(() => {
                const isOwnMessage = deleteMsg.sender === "supplier";
                const isRecent =
                  Date.now() - getTimestampMs(deleteMsg.timestamp) <
                  60 * 60 * 1000; // 1 hour

                if (isOwnMessage && isRecent) {
                  return (
                    <button onClick={handleDeleteForEveryone}>
                      <FaTrash /> Delete for everyone
                    </button>
                  );
                }
                return null;
              })()}

              <button onClick={() => setDeleteModalVisible(false)}>
                <FaTimes /> Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {showBulkMessage && (
        <div className="sc-bulk-message-modal">
          <div className="sc-bulk-message-content">
            <div className="sc-bulk-message-header">
              <div>
                <h3>Send Bulk Message</h3>
                <p>Send a message to {selectedChats.length} selected chats</p>
              </div>
              <button onClick={() => setShowBulkMessage(false)}>×</button>
            </div>
            <div className="sc-bulk-recipients">
              <h4>Recipients ({selectedChats.length})</h4>
              <div className="sc-recipients-list">
                {selectedChats.map((chat) => (
                  <div key={chat.uniqueId} className="sc-recipient-item">
                    <div className="sc-recipient-avatar">
                      <div className="sc-avatar-circle-small">
                        {chat.isGroup
                          ? "G"
                          : (chat.companyName.charAt(0) || "U").toUpperCase()}
                      </div>
                    </div>
                    <span className="sc-recipient-name">
                      {chat.isGroup ? `${chat.groupName}` : chat.companyName}
                    </span>
                    <button
                      className="sc-remove-recipient"
                      onClick={() => toggleChatSelection(chat)}
                      title="Remove from selection"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
            <div className="sc-bulk-message-form">
              <div className="sc-bulk-input-wrapper">
                <textarea
                  value={bulkMessage}
                  onChange={(e) => setBulkMessage(e.target.value)}
                  placeholder="Type your bulk message here..."
                  className="sc-bulk-message-input"
                />
                <input
                  ref={bulkFileInputRef}
                  type="file"
                  multiple
                  accept="image/png,image/jpg,image/jpeg,application/pdf"
                  onChange={handleBulkFileSelect}
                  style={{ display: "none" }}
                />
                <button
                  className="sc-attach-btn"
                  onClick={() => bulkFileInputRef.current?.click()}
                  title="Attach files"
                >
                  <IoAttachOutline />
                </button>
              </div>
              {bulkFiles.length > 0 && (
                <div className="sc-bulk-files-preview">
                  <h5>Attached Files ({bulkFiles.length})</h5>
                  <div className="sc-bulk-files-list">
                    {bulkFiles.map((file, index) => (
                      <div key={index} className="sc-bulk-file-item">
                        {file.type.startsWith("image/") ? (
                          <img
                            src={
                              URL.createObjectURL(file) || "/placeholder.svg"
                            }
                            alt={file.name}
                            className="sc-bulk-file-preview"
                          />
                        ) : (
                          <div className="sc-bulk-file-icon">
                            <IoDocumentOutline />
                          </div>
                        )}
                        <span className="sc-bulk-file-name">{file.name}</span>
                        <button
                          className="sc-remove-bulk-file"
                          onClick={() => removeBulkFile(index)}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="sc-bulk-message-actions">
                <button
                  className="sc-bulk-send-final-btn"
                  onClick={handleSendBulkMessage}
                  disabled={
                    isSendingBulk ||
                    (!bulkMessage.trim() && bulkFiles.length === 0)
                  }
                >
                  {isSendingBulk ? "Sending..." : "Send to All"}
                </button>
                <button
                  className="sc-bulk-cancel-btn"
                  onClick={() => setShowBulkMessage(false)}
                  disabled={isSendingBulk}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showUserProfile && selectedUserProfile && (
        <div className="sc-profile-modal">
          <div className="sc-profile-content">
            <div className="sc-profile-header">
              <h3>User Profile</h3>
              <button onClick={() => setShowUserProfile(false)}>×</button>
            </div>

            <div className="sc-profile-body">
              <div className="sc-profile-avatar">
                <div className="sc-avatar-circle-large">
                  {(
                    selectedUserProfile.company_name ||
                    selectedUserProfile.companyName ||
                    selectedUserProfile.name ||
                    "U"
                  )
                    .charAt(0)
                    .toUpperCase()}
                </div>
              </div>

              <div className="sc-profile-info">
                <h4>
                  {selectedUserProfile.company_name ||
                    selectedUserProfile.companyName ||
                    selectedUserProfile.name ||
                    "Unknown User"}
                </h4>

                <p>
                  <strong>Contact:</strong>{" "}
                  {selectedUserProfile.phone ||
                    selectedUserProfile.email ||
                    "Not provided"}
                </p>

                {/* Show Company only if it exists (Main / Firm) */}
                {selectedUserProfile.company_name && (
                  <p>
                    <strong>Company:</strong> {selectedUserProfile.company_name}
                  </p>
                )}

                {/* Show Email only if exists (Partner) */}
                {selectedUserProfile.email && (
                  <p>
                    <strong>Email:</strong> {selectedUserProfile.email}
                  </p>
                )}
              </div>

              <div className="sc-profile-actions">
                <button onClick={() => showMediaVisibility(selectedChat)}>
                  <IoImagesOutline /> Media Visibility
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showGroupProfile && selectedGroupProfile && (
        <div className="sc-profile-modal">
          <div className="sc-profile-content">
            <div className="sc-profile-header">
              <h3>Group Profile</h3>
              <button onClick={() => setShowGroupProfile(false)}>×</button>
            </div>
            <div className="sc-profile-body">
              <div className="sc-profile-avatar">
                <div className="sc-avatar-circle-large">G</div>
              </div>
              <div className="sc-profile-info">
                <h4>{selectedGroupProfile.groupName}</h4>
                <p>
                  <strong>Admin:</strong>{" "}
                  {selectedGroupProfile.groupAdmin === "supplier"
                    ? "You"
                    : memberProfiles[selectedGroupProfile.groupAdmin]?.name ||
                      selectedGroupProfile.groupAdmin}
                </p>
                <p>
                  <strong>Members:</strong>{" "}
                  {selectedGroupProfile.members?.length || 0}
                </p>
                <div className="sc-group-members">
                  {selectedGroupProfile.members?.map((member, index) => {
                    const profile = memberProfiles[member] || {
                      name: member,
                      phone: "Not provided",
                    };
                    return (
                      <div key={index} className="sc-member-item">
                        <span>
                          {profile.name} - {profile.phone}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="sc-profile-actions">
                <button onClick={() => setShowAddMember(true)}>
                  <IoAddOutline /> Add Member
                </button>
                <button onClick={() => setShowRemoveMember(true)}>
                  <IoTrashBinOutline /> Remove Member
                </button>
                <button
                  onClick={() =>
                    showMediaVisibility(selectedGroupProfile, "media")
                  }
                >
                  <IoImagesOutline /> Media Visibility
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showMediaModal && (
        <div className="sc-media-modal">
          <div className="sc-media-modal-content">
            <div className="sc-media-modal-header">
              <div className="sc-media-tabs">
                <button
                  className={`sc-media-tab ${mediaModalType === "media" ? "active" : ""}`}
                  onClick={() => setMediaModalType("media")}
                >
                  <IoImagesOutline /> Media ({chatMedia.media?.length || 0})
                </button>
                <button
                  className={`sc-media-tab ${mediaModalType === "docs" ? "active" : ""}`}
                  onClick={() => setMediaModalType("docs")}
                >
                  <IoDocumentOutline /> Documents ({chatMedia.docs?.length || 0}
                  )
                </button>
                <button
                  className={`sc-media-tab ${mediaModalType === "links" ? "active" : ""}`}
                  onClick={() => setMediaModalType("links")}
                >
                  <IoLinkOutline /> Links ({chatMedia.links?.length || 0})
                </button>
              </div>
              <button
                className="sc-media-close"
                onClick={() => setShowMediaModal(false)}
              >
                <IoCloseOutline />
              </button>
            </div>
            <div className="sc-media-modal-body">
              {mediaModalType === "media" && (
                <div className="sc-media-grid">
                  {chatMedia.media?.length > 0 ? (
                    chatMedia.media.map((item, index) => (
                      <div key={index} className="sc-media-item">
                        <img
                          src={item.url || "/placeholder.svg"}
                          alt={item.name}
                          className="sc-media-image"
                          onClick={() => window.open(item.url, "_blank")}
                        />
                        <div className="sc-media-info">
                          <span className="sc-media-name">{item.name}</span>
                          <span className="sc-media-date">
                            {formatTime(item.timestamp)}
                          </span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="sc-media-empty">No media files found</div>
                  )}
                </div>
              )}
              {mediaModalType === "docs" && (
                <div className="sc-docs-list">
                  {chatMedia.docs?.length > 0 ? (
                    chatMedia.docs.map((item, index) => (
                      <div key={index} className="sc-doc-item">
                        <div className="sc-doc-icon">
                          <IoDocumentOutline />
                        </div>
                        <div className="sc-doc-info">
                          <span className="sc-doc-name">{item.name}</span>
                          <span className="sc-doc-size">
                            {(item.size / 1024 / 1024).toFixed(2)} MB
                          </span>
                          <span className="sc-doc-date">
                            {formatTime(item.timestamp)}
                          </span>
                        </div>
                        <button
                          className="sc-doc-download"
                          onClick={() => window.open(item.url, "_blank")}
                        >
                          Download
                        </button>
                      </div>
                    ))
                  ) : (
                    <div className="sc-media-empty">No documents found</div>
                  )}
                </div>
              )}
              {mediaModalType === "links" && (
                <div className="sc-links-list">
                  {chatMedia.links?.length > 0 ? (
                    chatMedia.links.map((item, index) => (
                      <div key={index} className="sc-link-item">
                        <div className="sc-link-icon">
                          <IoLinkOutline />
                        </div>
                        <div className="sc-link-info">
                          <a
                            href={item.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="sc-link-url"
                          >
                            {item.url}
                          </a>
                          <span className="sc-link-message">
                            {item.messageText}
                          </span>
                          <span className="sc-link-date">
                            {formatTime(item.timestamp)}
                          </span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="sc-media-empty">No links found</div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {showCreateGroup && (
        <div className="sc-create-group-modal">
          <div className="sc-create-group-content">
            <div className="sc-create-group-header">
              <h3>Create New Group</h3>
              <button onClick={() => setShowCreateGroup(false)}>×</button>
            </div>
            <div className="sc-create-group-form">
              <div className="sc-form-group">
                <label>Group Name</label>
                <input
                  type="text"
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  placeholder="Enter group name..."
                  className="sc-group-input"
                />
              </div>
              <div className="sc-form-group">
                <label>Select Users</label>
                <div className="sc-users-list">
                  {chats.map((chat) => (
                    <div key={chat.userId} className="sc-user-item">
                      <input
                        className="sc-checkbox"
                        type="checkbox"
                        id={`user-${chat.userId}`}
                        checked={selectedGroupUsers.some(
                          (u) => u.userId === chat.userId,
                        )}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedGroupUsers((prev) => [
                              ...prev,
                              { userId: chat.userId, name: chat.companyName },
                            ]);
                          } else {
                            setSelectedGroupUsers((prev) =>
                              prev.filter((u) => u.userId !== chat.userId),
                            );
                          }
                        }}
                      />
                      <label htmlFor={`user-${chat.userId}`}>
                        {chat.companyName}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
              <div className="sc-create-group-actions">
                <button
                  className="sc-group-create-btn"
                  onClick={handleCreateGroup}
                  disabled={
                    !newGroupName.trim() || selectedGroupUsers.length === 0
                  }
                >
                  Create Group
                </button>
                <button
                  className="sc-group-cancel-btn"
                  onClick={() => setShowCreateGroup(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showTemplates && (
        <div className="sc-templates-modal">
          <div className="sc-templates-content">
            <div className="sc-templates-header">
              <h3>Message Templates</h3>
              <button onClick={() => setShowTemplates(false)}>×</button>
            </div>
            <button
              className="sc-add-template-btn"
              onClick={() => setShowCreateTemplate(true)}
            >
              Add New Template
            </button>
            <div className="sc-templates-list">
              {templates.map((template) => (
                <div key={template.id} className="sc-template-item">
                  <h4>{template.title}</h4>
                  <p>{template.message.substring(0, 100)}...</p>
                  <div className="sc-template-actions">
                    <button onClick={() => handleUseTemplate(template)}>
                      Use
                    </button>
                    <button onClick={() => handleEditTemplate(template)}>
                      Edit
                    </button>
                    <button onClick={() => handleDeleteTemplate(template.id)}>
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {showCreateTemplate && (
        <div className="sc-create-template-modal">
          <div className="sc-create-template-content">
            <div className="sc-create-template-header">
              <h3>
                {editingTemplate ? "Edit Template" : "Create New Template"}
              </h3>
              <button
                onClick={() => {
                  setShowCreateTemplate(false);
                  setEditingTemplate(null);
                }}
              >
                ×
              </button>
            </div>
            <div className="sc-form-group">
              <label>Title</label>
              <input
                type="text"
                value={
                  editingTemplate ? editingTemplate.title : newTemplate.title
                }
                onChange={(e) =>
                  editingTemplate
                    ? setEditingTemplate({
                        ...editingTemplate,
                        title: e.target.value,
                      })
                    : setNewTemplate({ ...newTemplate, title: e.target.value })
                }
                placeholder="Enter template title..."
              />
            </div>
            <div className="sc-form-group">
              <label>Message</label>
              <textarea
                value={
                  editingTemplate
                    ? editingTemplate.message
                    : newTemplate.message
                }
                onChange={(e) =>
                  editingTemplate
                    ? setEditingTemplate({
                        ...editingTemplate,
                        message: e.target.value,
                      })
                    : setNewTemplate({
                        ...newTemplate,
                        message: e.target.value,
                      })
                }
                placeholder="Enter template message..."
              />
            </div>
            <button onClick={handleSaveTemplate}>Save</button>
          </div>
        </div>
      )}
      {showAddMember && (
        <div className="sc-add-member-modal">
          <div className="sc-add-member-content">
            <div className="sc-add-member-header">
              <h3>Add Members</h3>
              <button onClick={() => setShowAddMember(false)}>×</button>
            </div>
            <div className="sc-users-list">
              {chats
                .filter(
                  (chat) => !selectedGroupProfile.members.includes(chat.userId),
                )
                .map((chat) => (
                  <div key={chat.userId} className="sc-user-item">
                    <input
                      type="checkbox"
                      id={`add-user-${chat.userId}`}
                      checked={selectedMembersToAdd.includes(chat.userId)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedMembersToAdd((prev) => [
                            ...prev,
                            chat.userId,
                          ]);
                        } else {
                          setSelectedMembersToAdd((prev) =>
                            prev.filter((u) => u !== chat.userId),
                          );
                        }
                      }}
                    />
                    <label htmlFor={`add-user-${chat.userId}`}>
                      {chat.companyName}
                    </label>
                  </div>
                ))}
            </div>
            <button
              onClick={handleAddMembers}
              disabled={selectedMembersToAdd.length === 0}
            >
              Add Selected
            </button>
          </div>
        </div>
      )}
      {showRemoveMember && (
        <div className="sc-remove-member-modal">
          <div className="sc-remove-member-content">
            <div className="sc-remove-member-header">
              <h3>Remove Members</h3>
              <button onClick={() => setShowRemoveMember(false)}>×</button>
            </div>
            <div className="sc-users-list">
              {selectedGroupProfile.members.map((member) => (
                <div key={member} className="sc-user-item">
                  <input
                    type="checkbox"
                    id={`remove-user-${member}`}
                    checked={selectedMembersToRemove.includes(member)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedMembersToRemove((prev) => [...prev, member]);
                      } else {
                        setSelectedMembersToRemove((prev) =>
                          prev.filter((u) => u !== member),
                        );
                      }
                    }}
                  />
                  <label htmlFor={`remove-user-${member}`}>
                    {memberProfiles[member]?.name || member}
                  </label>
                </div>
              ))}
            </div>
            <button
              onClick={handleRemoveMembers}
              disabled={selectedMembersToRemove.length === 0}
            >
              Remove Selected
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupplierChatbot;
